import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionHeading } from './ui/SectionHeading';
import { previewPages } from '../data/previews';
import { productName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = (index + total) % total;
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const childCenter = el.offsetLeft - track.offsetLeft + el.clientWidth / 2;
        const d = Math.abs(childCenter - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="border-t border-primary/10 bg-background py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-94" data-mp="1wc4q1rfrjx34">
      <div className="mx-auto max-w-6xl px-4" data-id="element-95" data-mp="3vpijxkcc8xgv">
        <div className="text-center" data-id="element-96" data-mp="3vo7hpoyjjxrv">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-accent" data-id="element-97" data-mp="3qq5quhuviu5u">Look Inside</p>
          <SectionHeading id="inside-title" className="mt-3" data-id="element-98" data-mp="1p089160tv4rg">
            Discover What&apos;s Inside <span className="text-accent" data-id="element-99" data-mp="1tr6s2dd7agye">{productName}</span>
          </SectionHeading>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-muted" data-id="element-100" data-mp="1f2kdwezb7v8r">
            Solar energy fundamentals, photovoltaic technology, solar panels, PV system components, inverters, battery
            storage, on-grid and off-grid systems, solar PV design, system sizing, energy calculations, installation
            principles, electrical safety, commissioning, preventive maintenance, troubleshooting, performance analysis,
            energy efficiency, advanced solar applications, practical case studies, and step-by-step technical
            explanations.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages from the professional edition" data-id="element-101" data-mp="nbe8pk4qf7cm">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-5 md:px-[4%] [&::-webkit-scrollbar]:hidden" data-id="element-102" data-mp="re139sssf6di">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-surface shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-primary opacity-100' : 'border-primary/25 opacity-80'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-103" data-mp="trmseigk182q" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-104" data-mp="3s75xni8evm79" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary/60 bg-surface text-foreground shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-surface-hover focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/50 sm:-left-2" aria-label="Previous page" data-id="element-105" data-mp="61sjud39q5oa">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-106" data-mp="2gtini68sf01l" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary/60 bg-surface text-foreground shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-surface-hover focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/50 sm:-right-2" aria-label="Next page" data-id="element-107" data-mp="2lrag72h9aq6u">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-108" data-mp="34jcyei9jqej7" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-109" data-mp="39fk90ocw0e7h">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${index === active ? 'w-6 bg-accent' : 'w-2 bg-primary/50 hover:bg-primary'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-110" data-mp="1a6ddc9mecs4j" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}